import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
} from "react";
import classNames from "classnames";
import { Dropdown as SDropdown } from "semantic-ui-react";
import { checkTagEmailsValid, searchUsers } from "./actions";
import { useDispatch } from "react-redux";

// same as one used in cinnamon
const emailRegex = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);

// characters that can never legitimately appear inside an email address, so
// are safe to treat as separators when splitting a block of pasted text
const pasteSeparatorRegex = /[\r\n,;\t]+/;

// a leading bullet glyph, or a hyphen/asterisk immediately followed by
// whitespace (list markers), but never a bare -/* since both are valid
// (if uncommon) characters inside a real email local-part
const listMarkerRegex = /^(?:[•◦‣·▪]+\s*|[-*]\s+)/;

// splits a hard-separated segment further on plain whitespace, once any
// leading list marker has already been stripped - stripping the marker
// first keeps a "- " prefix from becoming its own orphaned, invalid token
const whitespaceSplitRegex = /\s+/;

const cleanPastedToken = (rawToken) =>
  rawToken.trim().replace(listMarkerRegex, "").trim().toLowerCase();

// the selected options, in the shape the parent component consumes them
const buildItems = (selections, options) =>
  options
    .filter((x) => !!x && selections.some((s) => s === x.value))
    .map((x) => ({
      id: x.value,
      email: x.text,
      firstName: x.firstName || "",
      lastName: x.lastName || "",
      isNew: x.value.includes("@"),
    }));

const pasteFailureMessage = (count) =>
  `${count} pasted email${
    count > 1 ? "s" : ""
  } could not be added (invalid or already in use)`;

export const TagInput = React.forwardRef(function TagInput(
  {
    allowExistingUsers,
    label,
    onUpdated = () => {},
    customError,
    className,
    searchParams,
    ...props
  },
  ref
) {
  const dispatch = useDispatch();
  const [selections, setSelections] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [manualOptions, setManualOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // report the current users to the parent component
    onUpdated(buildItems(selections, manualOptions));
  }, [selections, manualOptions]);

  // validates a single email and adds it to the selections, resolving with the
  // resulting items so a caller can act on them without waiting for a re-render
  const commitEmail = (rawValue) => {
    const currentItems = buildItems(selections, manualOptions);
    const email = (rawValue || "").trim().toLowerCase();

    if (!email) {
      return Promise.resolve(currentItems);
    }

    if (!emailRegex.test(email)) {
      setErrors([
        "Not a valid email address. Use the enter or return key to enter each address if typing multiples.",
      ]);
      return Promise.resolve(currentItems);
    }

    if (selections.some((x) => x === email)) {
      setSearch("");
      return Promise.resolve(currentItems);
    }

    setCheckingEmail(true);
    return dispatch(checkTagEmailsValid([email]))
      .then(({ response, success }) => (success ? response || [] : [email]))
      .catch(() => [email])
      .then((inUseEmails) => {
        if (inUseEmails.includes(email)) {
          setErrors(["Email address is already in use"]);
          return currentItems;
        }

        // merged against the latest state rather than the render-time snapshot,
        // so a resolution can't drop an email added while this check was in
        // flight, and can't add the same one twice
        const addSelection = (prev) =>
          prev.some((x) => x === email) ? prev : [...prev, email];
        const addOption = (prev) =>
          prev.some((x) => !!x && x.value === email)
            ? prev
            : [...prev, { text: email, value: email }];

        setSelections(addSelection);
        setManualOptions(addOption);
        setSearch("");
        setErrors([]);
        return buildItems(addSelection(selections), addOption(manualOptions));
      })
      .finally(() => setCheckingEmail(false));
  };

  // lets the parent add an email that was typed but never committed with the
  // return key - submitting straight after typing then behaves the same way
  useImperativeHandle(ref, () => ({
    commitPendingEmail: () =>
      checkingEmail
        ? Promise.resolve(buildItems(selections, manualOptions))
        : commitEmail(search),
  }));

  const onAdd = (e, { value }) => {
    if (checkingEmail) {
      return;
    }

    commitEmail(value);
  };

  const onPaste = (e) => {
    if (checkingEmail) {
      return;
    }

    const text =
      e.clipboardData?.getData("text/plain") ||
      e.clipboardData?.getData("text");
    if (!text) {
      return;
    }

    const seen = new Set(selections);
    const tokens = [];
    text.split(pasteSeparatorRegex).forEach((rawSegment) => {
      cleanPastedToken(rawSegment)
        .split(whitespaceSplitRegex)
        .forEach((token) => {
          if (token && !seen.has(token)) {
            seen.add(token);
            tokens.push(token);
          }
        });
    });

    if (tokens.length === 0) {
      return;
    }

    e.preventDefault();
    setSearch("");
    setErrors([]);

    const formatValidEmails = tokens.filter((email) => emailRegex.test(email));
    const formatInvalidCount = tokens.length - formatValidEmails.length;

    if (formatValidEmails.length === 0) {
      if (formatInvalidCount > 0) {
        setErrors([pasteFailureMessage(formatInvalidCount)]);
      }
      return;
    }

    // one batched request to check which of these are already in use -
    // still re-checked authoritatively server-side on final submission
    // (someone else could create a matching user in the meantime), this is
    // just to give paste the same early feedback typing already has via
    // onAdd, without a request per pasted address
    setCheckingEmail(true);
    dispatch(checkTagEmailsValid(formatValidEmails))
      .then(({ success, response }) =>
        success ? response || [] : formatValidEmails
      )
      .catch(() => formatValidEmails)
      .then((inUseEmails) => {
        const alreadyInUse = new Set(inUseEmails);
        const validEmails = formatValidEmails.filter(
          (email) => !alreadyInUse.has(email)
        );

        if (validEmails.length > 0) {
          setSelections((prev) => [...prev, ...validEmails]);
          setManualOptions((prev) => [
            ...prev,
            ...validEmails.map((email) => ({ text: email, value: email })),
          ]);
        }

        const failedCount =
          formatInvalidCount + (formatValidEmails.length - validEmails.length);
        if (failedCount > 0) {
          setErrors([pasteFailureMessage(failedCount)]);
        }
      })
      .finally(() => setCheckingEmail(false));
  };

  const onChange = (e, { value }) => {
    if (checkingEmail) {
      return;
    }

    const diff = value.length - selections.length;

    if (diff > 0) {
      // adding new selection - only interested in selections of existing users (new emails are handled in onAdd)
      const item = value.filter((x) => !selections.some((y) => y === x))[0];
      if (!item.includes("@")) {
        setSelections(value);
        setSearch("");
        setErrors([]);
        setManualOptions([
          ...manualOptions.filter((x) => !!x),
          userOptions.find((x) => x.value === item),
        ]);
      }
    } else if (diff < 0) {
      //removing selection
      setManualOptions(
        manualOptions.filter((x) => !!x && value.some((y) => y === x.value))
      );
      setSelections(value);
    }
  };

  const onSearch = (e, { searchQuery }) => {
    setSearch(searchQuery);
    if (allowExistingUsers) {
      setLoadingEmails(true);
      dispatch(searchUsers(searchQuery, searchParams))
        .then(({ success, response }) => {
          if (success) {
            setUserOptions(
              response.results.map((x) => ({
                value: x.id,
                text: x.email,
                firstName: x.firstName,
                lastName: x.lastName,
              }))
            );
          }
        })
        .finally(() => setLoadingEmails(false));
    }
  };

  const allOptions = useMemo(() => {
    return [
      ...userOptions,
      ...manualOptions.filter(
        (x) => !!x && userOptions.every((o) => o.value !== x.value)
      ),
    ].map((x) => ({ value: x.value, text: x.text }));
  }, [userOptions, manualOptions]);

  return (
    <section
      className={classNames(
        "tag-container",
        className,
        customError || errors.length > 0 ? "has-error" : null
      )}
      {...props}
      onPaste={onPaste}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <label>{label}</label>
        <span className="error">
          {errors.length > 0 ? errors[0] : customError}
        </span>
      </div>
      <SDropdown
        options={allOptions}
        placeholder="Enter email"
        search
        selection
        fluid
        multiple
        allowAdditions={userOptions.length === 0 && !loadingEmails}
        value={selections}
        onAddItem={onAdd}
        onChange={onChange}
        onSearchChange={onSearch}
        searchQuery={search}
        loading={loadingEmails || checkingEmail}
      />
    </section>
  );
});

export default TagInput;
